import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchLoadFeed = createAsyncThunk(
  'loadFeed/fetchLoadFeed',
  async ({ page = 1, perPage = 10, sortBy = 'created_at', sortOrder = 'desc' }, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/loads/feed', {
        params: { page, per_page: perPage, sort_by: sortBy, sort_order: sortOrder }
      });
      console.log('Load feed response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching load feed:', error);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchSavedLoads = createAsyncThunk(
  'loadFeed/fetchSavedLoads',
  async ({ page = 1, perPage = 10 }, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/loads/saved', {
        params: { page, per_page: perPage }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved loads:', error);
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

const loadFeedSlice = createSlice({
  name: 'loadFeed',
  initialState: {
    loads: [],
    savedLoads: [],
    currentView: 'feed',
    status: 'idle',
    error: null,
    pagination: {
      total: 0,
      pages: 0,
      page: 1,
      perPage: 10,
      nextPage: null
    },
    lastSyncTimestamp: null
  },
  reducers: {
    setLastSyncTimestamp: (state, action) => {
      state.lastSyncTimestamp = action.payload;
    },
    newLoadReceived: (state, action) => {
      // Ensure the new load has a bids array
      const newLoad = {
        ...action.payload,
        bids: action.payload.bids || []
      };
      state.loads.unshift(newLoad);
      state.pagination.total += 1;
    },
    updateLoad: (state, action) => {
      const index = state.loads.findIndex(load => load.id === action.payload.id);
      const savedIndex = state.savedLoads.findIndex(load => load.id === action.payload.id);
      
      // Handle assigned status first - it should remove from both feeds
      // Normalize status to lowercase for consistent comparison
      const normalizedStatus = (action.payload.status || '').toLowerCase();
      if (normalizedStatus === 'assigned') {
        // Remove from main feed
        if (index !== -1) {
          state.loads.splice(index, 1);
          state.pagination.total -= 1;
        }
        // Remove from saved loads
        if (savedIndex !== -1) {
          state.savedLoads.splice(savedIndex, 1);
        }
        return; // Exit early since load is removed from both places
      }

      // Handle save/unsave actions
      if (action.payload.action === 'saved') {
        // Only move this specific load to saved loads
        if (index !== -1) {
          const loadToSave = state.loads[index];
          // Add to saved loads if not already there
          if (savedIndex === -1) {
            state.savedLoads.unshift({
              ...loadToSave,
              ...action.payload,
              is_saved: true
            });
          } else {
            // Update existing saved load
            state.savedLoads[savedIndex] = {
              ...state.savedLoads[savedIndex],
              ...action.payload,
              is_saved: true
            };
          }
          // Keep the load in the main feed but update its saved status
          state.loads[index] = {
            ...loadToSave,
            is_saved: true
          };
        }
      } else if (action.payload.action === 'unsaved') {
        // Remove from saved loads
        if (savedIndex !== -1) {
          state.savedLoads.splice(savedIndex, 1);
        }
        
        // Add to main feed if not already there
        if (index === -1) {
          state.loads.unshift({
            ...action.payload,
            is_saved: false
          });
          state.pagination.total += 1;
        } else {
          // Update existing load in feed
          state.loads[index] = {
            ...state.loads[index],
            is_saved: false
          };
        }
      } else {
        // Handle other updates
        if (index !== -1) {
          state.loads[index] = {
            ...state.loads[index],
            ...action.payload,
            stops: action.payload.stops || state.loads[index].stops || [],
            bids: action.payload.bids || state.loads[index].bids || []
          };
        }
        
        // Update in saved loads if it exists there
        if (savedIndex !== -1) {
          state.savedLoads[savedIndex] = {
            ...state.savedLoads[savedIndex],
            ...action.payload,
            stops: action.payload.stops || state.savedLoads[savedIndex].stops || [],
            bids: action.payload.bids || state.savedLoads[savedIndex].bids || [],
            is_saved: true
          };
        }
      }
    },
    deleteLoad: (state, action) => {
      state.loads = state.loads.filter(load => load.id !== action.payload);
      state.pagination.total -= 1;
    },
    addBidToLoad: (state, action) => {
      const { loadId, bid } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        const load = state.loads[loadIndex];
        if (!load.bids) {
          load.bids = [];
        }
        load.bids.push(bid);
      }
    },
    updateBidInLoad: (state, action) => {
      const { loadId, bid } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        const load = state.loads[loadIndex];
        if (load.bids) {
          const bidIndex = load.bids.findIndex(b => b.id === bid.id);
          if (bidIndex !== -1) {
            load.bids[bidIndex] = bid;
          }
        }
      }
    },
    
    updateBidStatus: (state, action) => {
      const { loadId, bidId, status } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        const load = state.loads[loadIndex];
        if (load.bids) {
          const bidIndex = load.bids.findIndex(b => b.id === bidId);
          if (bidIndex !== -1) {
            load.bids[bidIndex].status = status;
          }
        }
      }
    },
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    
    // Remove a load that has expired (called by expiry timer)
    removeExpiredLoad: (state, action) => {
      const loadId = action.payload;
      const index = state.loads.findIndex(load => load.id === loadId);
      if (index !== -1) {
        state.loads.splice(index, 1);
        state.pagination.total = Math.max(0, state.pagination.total - 1);
      }
      // Also remove from saved loads if present
      const savedIndex = state.savedLoads.findIndex(load => load.id === loadId);
      if (savedIndex !== -1) {
        state.savedLoads.splice(savedIndex, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLoadFeed.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLoadFeed.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loads = action.payload.loads || [];
        state.pagination = {
          total: action.payload.total || 0,
          pages: action.payload.pages || 0,
          page: action.payload.page || 1,
          perPage: action.payload.per_page || 10,
          nextPage: action.payload.next_page || null
        };
        state.lastSyncTimestamp = Date.now();
      })
      .addCase(fetchLoadFeed.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'An error occurred';
      })
      .addCase(fetchSavedLoads.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSavedLoads.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.savedLoads = action.payload.loads;
        state.pagination = {
          total: action.payload.total,
          pages: action.payload.pages,
          page: action.payload.page,
          perPage: action.payload.per_page,
          nextPage: action.payload.next_page
        };
      })
      .addCase(fetchSavedLoads.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'An error occurred';
      });
  },
});

export const { 
  setLastSyncTimestamp, 
  newLoadReceived, 
  updateLoad, 
  deleteLoad, 
  addBidToLoad, 
  updateBidInLoad, 
  updateBidStatus,
  setCurrentView,
  removeExpiredLoad
} = loadFeedSlice.actions;
export default loadFeedSlice.reducer;

export const selectFeedLoads = (state) => state.loadFeed.loads;
export const selectFeedLoadById = (state, loadId) => {
  const load = selectFeedLoads(state).find(load => load.id === loadId);
  console.log('selectFeedLoadById:', { loadId, load });
  return load;
};

export const selectFeedBidById = (state, loadId, bidId) => {
  const userBids = state.loadFeed.userBids[loadId];
  return userBids ? userBids.find(bid => bid.id === bidId) : undefined;
};

export const selectUserBidsForLoad = (state, loadId) => {
  const load = state.loadFeed.loads.find(load => load.id === loadId);
  const currentUserId = state.auth.user.id;
  return load ? load.bids.filter(bid => bid.bidder_id === currentUserId) : [];
};
